<template lang="pug">
v-container.pa-8(style="max-width:1080px")
  div.d-flex.justify-center.mt-16(v-if="isFetching")
    v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

  template(v-else)
    v-row(no-gutters align="center")
      v-col.shrink
        v-btn.ml-n3(text icon @click="goToProvider" height)
          v-icon(color="basic800" :size="24") mdi-arrow-left
      v-col.ml-3
        .text-2xl.font-semibold.neutral700--text {{ id ? $t('edit_provider') : $t('create_new_provider') }}

    v-form(ref="form")
      provider-details(:errorType.sync="errorType")
      v-divider.basic500.my-5
      provider-availability
      v-divider.basic500.my-5
      provider-service

    v-footer.white(app inset padless elevation="3")
      v-container.px-8.py-2
        div.d-flex.align-center
          v-spacer
          y-button(:label="id ? $t('save') : $t('finish')" color="neutral700" dark :loading="isLoading" @click="saveData")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ProviderDetails from '@/components/clinic/manage/ProviderDetails.vue'
import ProviderAvailability from '@/components/clinic/manage/ProviderAvailability.vue'
import ProviderService from '@/components/clinic/manage/ProviderService.vue'

export default {
  name: 'Provider',
  components: { ProviderDetails, ProviderAvailability, ProviderService },
  props: ['id'],
  data () {
    return {
      isFetching: false,
      isLoading: false,
      errorType: false,
      manageTab: 0
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      form: 'clinic/getForm',
      tempProvider: 'clinic/getTempProvider'
    }),
  },
  mounted () {
    if (this.id) {
      this.fetchProvider()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchProvider () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/providers/${this.id}`)
        .then(resp => {
          this.$store.commit('clinic/setProvider', resp.data)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    saveData () {
      if (!this.tempProvider.type) {
        this.errorType = true
      }
      if (!this.$refs.form.validate() || !this.tempProvider.type) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      this.isLoading = true
      var params = new FormData()
      if (this.tempProvider.profile_image) {
        params.append('profile_image', this.tempProvider.profile_image)
      }
      let data = JSON.parse(JSON.stringify(this.tempProvider))
      delete data.profile_image
      const endpoint = `/clinics/${this.company.slug}/providers`
      if (this.id) {
        const endpoint = `/clinics/${this.company.slug}/providers/${this.id}`
        this.$axios.patch(endpoint, data)
          .then(resp => {
            this.updateActivityLog2(endpoint, data, resp, 'PATCH', this.id)
            if (this.tempProvider.profile_image) {
              let provId = resp.data.id
              this.$axios.patch(`/clinics/${this.company.slug}/providers/${provId}`, params)
                .catch(() => {
                  this.showMessage(['error', this.$t('error_message.unable_to_upload_image')])
                })
                .finally(() => {
                  this.goToProvider()
                  this.isLoading = false
                })
            } else {
              this.goToProvider()
            }
          })
          .catch(err => {
            let message = this.$t('error_message.unable_to_complete_request')
            if (err.response.data) {
              if (err.response.data.detail) {
                message = err.response.data.detail
              } else {
                let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
                if (data) { message = data  }
              }
            }
            this.showMessage(['error', message])
          })
          .finally(() => {
            if (!this.tempProvider.profile_image) {
              this.isLoading = false
            }
          })
      } else {
        this.$axios.post(endpoint, data)
          .then(resp => {
            this.updateActivityLog(endpoint, data, resp, 'POST')
            if (this.tempProvider.profile_image) {
              let provId = resp.data.id
              this.$axios.patch(`/clinics/${this.company.slug}/providers/${provId}`, params)
                .catch(() => {
                  this.showMessage(['error', this.$t('error_message.unable_to_upload_image')])
                })
                .finally(() => {
                  this.goToProvider()
                  this.isLoading = false
                })
            } else {
              this.goToProvider()
            }
          })
          .catch(err => {
            let message = this.$t('error_message.unable_to_complete_request')
            if (err.response.data) {
              if (err.response.data.detail) {
                message = err.response.data.detail
              } else {
                let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
                if (data) { message = data  }
              }
            }
            this.showMessage(['error', message])
          })
          .finally(() => {
            if (!this.tempProvider.profile_image) {
              this.isLoading = false
            }
          })
      }
    },
    goToProvider () {
      this.$router.push({ path: '/admin/clinic/manage', name: 'ClinicPageSetup', params: { fromTab: 2 } })
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'provider',
        activity: 'Create new provider',
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    updateActivityLog2 (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'provider',
        activity: 'Update provider',
        raw_data: { endpoint, payload, response: response ? response.data : null, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  },
  destroyed () {
    this.$store.commit('clinic/resetProvider')
    this.$store.commit('clinic/resetTempProvider')
  }
}
</script>

<style scoped>
.clickable {
  cursor: pointer;
}
.main-container {
  position: relative;
}
.separator {
  position: absolute;
  height:2px;
  top: 38px;
  left: 0px;
  right: 0px;
}
</style>